/** Корневой компонент: поднимает конфиг и сессию, дальше — вход или карта. */

import { useCallback, useEffect, useState } from 'react';

import type { ConfigResponse, MeResponse } from '@cdf/shared';

import { ApiRequestError, api } from './api/client';
import { ErrorState } from './components/StateBlocks';
import { LoginScreen } from './screens/LoginScreen';
import { MainScreen } from './screens/MainScreen';

type Bootstrap =
  | { status: 'loading' }
  | { status: 'error'; error: ApiRequestError }
  | { status: 'ready'; config: ConfigResponse; me: MeResponse };

export function App(): JSX.Element {
  const [state, setState] = useState<Bootstrap>({ status: 'loading' });

  const bootstrap = useCallback(async (signal?: AbortSignal) => {
    setState({ status: 'loading' });
    try {
      // Конфиг и сессия независимы — тянем разом, экран ждёт оба.
      const [config, me] = await Promise.all([
        api.getConfig(signal),
        api.getMe(signal),
      ]);
      if (signal?.aborted) return;
      setState({ status: 'ready', config, me });
    } catch (error) {
      if (signal?.aborted) return;
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setState({
        status: 'error',
        error:
          error instanceof ApiRequestError
            ? error
            : new ApiRequestError(0, 'unknown', 'Сервис недоступен'),
      });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void bootstrap(controller.signal);
    return () => controller.abort();
  }, [bootstrap]);

  const refreshSession = useCallback(async () => {
    try {
      const me = await api.getMe();
      setState((prev) =>
        prev.status === 'ready' ? { ...prev, me } : prev,
      );
    } catch {
      // Сессию не подтвердили — вернёмся к входу.
      setState((prev) =>
        prev.status === 'ready'
          ? { ...prev, me: { authenticated: false, mock: prev.config.mock } }
          : prev,
      );
    }
  }, []);

  if (state.status === 'loading') {
    return (
      <div className="splash" role="status" aria-live="polite">
        <div className="splash__logo" aria-hidden="true">
          ⛽
        </div>
        <p className="splash__text">Поднимаем маршрут…</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="splash splash--error">
        <ErrorState
          title={state.error.isNetwork ? 'Нет связи' : 'Сервис не отвечает'}
          text={state.error.message}
          onRetry={() => void bootstrap()}
        />
      </div>
    );
  }

  if (!state.me.authenticated) {
    return (
      <LoginScreen
        mock={state.config.mock || state.me.mock}
        onAuthenticated={() => void refreshSession()}
      />
    );
  }

  return (
    <MainScreen
      config={state.config}
      me={state.me}
      onLoggedOut={() => void refreshSession()}
    />
  );
}
